import translate from "@insite/client-framework/Translate";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import loadProductAccessories from "@NA/Handlers/LoadProductAccessories";
import ProductImage from "@insite/content-library/Components/ProductImage";
import { Button, Clickable, Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { css } from "styled-components";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import ADIColors from "@NA/Utils/ADIColors";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { StyledButton } from "@insite/mobius/Clickable";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

interface OwnProps {
    cartLine: CartLineModel;
    extendedStyles?: MiniCartAccessoriesStyles;
    handleModalHeight?: (arg: boolean) => void;
}

export interface MiniCartAccessoriesStyles {
    accessoriesTitle?: TypographyPresentationProps;
    modelNumberErpNumberStyles?: TypographyPresentationProps;
    productCategoryStyles?: LinkPresentationProps;
    productNameStyles?: TypographyPresentationProps;
    productNameItemStyles?: InjectableCss;
    gridItems?: GridItemProps;
    productCategoryNameGridItem?: GridItemProps;
    viewProductButtonStyles?: ButtonPresentationProps;
    productImgGridItem?: GridItemProps;
}

export const miniCartAccessoriesStyles: MiniCartAccessoriesStyles = {
    accessoriesTitle: {
        size: 32,
        css: css`
            margin-top: 20px;
            margin-bottom: 30px;
            line-height: normal;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: 22px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    modelNumberErpNumberStyles: {
        size: 10,
        weight: 600,
        color: ADIColors.gray4,
        css: css`
            text-align: center;
            flex: 0 0 100%;
        `,
    },
    productCategoryStyles: {
        color: "black",
        typographyProps: {
            size: 11,
            css: css`
            font-size: 11px;
            font-weight: 600;
            line-height: normal;
            letter-spacing: normal;
            color: #2d2926;
        `,
        },
        hoverStyle: {
            textDecoration: "underline",
        },
    },
    gridItems: {
        css: css`
            flex-wrap: wrap;
            flex-basis: revert;
            align-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        > ${StyledButton} > img {
                            height: 90px !important;
                            width: 90px !important;
                            margin: 0 10px 0;
                        }
                    `,
                    css`
                        > ${StyledButton} > img {
                            height: 90px !important;
                            width: 90px !important;
                            margin: 0 10px 0;
                        }
                    `,
                    css`
                        > ${StyledButton} > img {
                            height: 90px !important;
                            width: 90px !important;
                            margin: 0 10px 0;
                        }
                    `,
                    null,
                ])}

           
        `,
    },
    productCategoryNameGridItem: {
        css: css`
            flex-wrap: wrap;
            flex-direction: column;
            height: 130px !important;
        `,
    },
    viewProductButtonStyles: {
        variant: "secondary",
        css: css`
            width: 218px;
            border-radius: 4px;
            border: solid 1px #004da6;
            background-color: #fff;
            color: #004da6;
            &:hover{
                background-color: #c8ddf6;
            }
        `,
    },
    productImgGridItem: {
        css: css`
            flex-wrap: wrap;
            flex-basis: revert;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        > ${StyledButton} > img {
                            height: 90px !important;
                            width: 90px !important;
                            margin: 0 10px 0;
                        }
                    `,
                    css`
                        > ${StyledButton} > img {
                            height: 90px !important;
                            width: 90px !important;
                            margin: 0 10px 0;
                        }
                    `,
                    css`
                        > ${StyledButton} > img {
                            height: 90px !important;
                            width: 90px !important;
                            margin: 0 10px 0;
                        }
                    `,
                    null,
                ])}

            
        `,
    },
    productNameItemStyles:{
        css: css`
        min-height: 65px;
    `,
    },
    productNameStyles: {
        css: css`
            width: 100%;
            min-height: initial;
            display: -webkit-box;
            line-clamp: 3;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            width: 100%;
            font-size: 14px;
            font-weight: 600;
            line-height: normal;
            letter-spacing: normal;
            color: #2d2926;
            margin-top: 4px;
        `,
    },
};

const mapStateToProps = (state: CustomApplicationState) => {
    const currentPage = getCurrentPage(state).type;
    const isPlp = currentPage === "ProductListPage";
    const isPdp = currentPage === "ProductDetailsPage";
    const isCheckout = currentPage === "CheckoutReviewAndSubmitPage";
    const getFilteredAcc = (accessories: any, accessoriesAddedInCart: any) =>
        accessories?.filter((acc: any) =>
            accessoriesAddedInCart.every((accInCart: any) => acc.productDto?.erpNumber !== accInCart.erpNumber),
        );
    return {
        accessories: getFilteredAcc(
            state.ADIGlobalData.productAccessoriesStore.productAccessoriesObj?.productAccessories,
            state.data.carts.byId.current.cartLines,
        ),
        catalogSettingSettingsSettings: (state.context.settings.settingsCollection as any).catalogSettingSettings,
        recommendedProducts: state.ADIGlobalData.PdpRecommendeProductsStore.RecommendedProductsData,
        isPlp,
        isPdp,
        isCheckout,
    };
};

const mapDispatchToProps = {
    loadProductAccessories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const MiniCartAccessories: FC<Props> = ({
    cartLine,
    accessories,
    recommendedProducts,
    catalogSettingSettingsSettings,
    loadProductAccessories,
    history,
    extendedStyles,
    handleModalHeight,
    isPlp,
    isPdp,
    isCheckout,
}) => {
    const [styles] = React.useState(() => mergeToNew(miniCartAccessoriesStyles, extendedStyles));

    const onClick = (link: string | undefined) => {
        if (link) {
            history.push(link);
        }
    };

    useEffect(() => {
        //ADINA-52733 : load accessorieswidget when cart modal appears
        if (!isPlp && !isPdp && !isCheckout) {
            loadProductAccessories({ erpNumber: cartLine.erpNumber });
        }
    }, []);

    useEffect(() => {
        if (handleModalHeight) {
            if (
                accessories?.length &&
                accessories?.length >= catalogSettingSettingsSettings.minimumAccessoryProductCount
            ) {
                handleModalHeight(true);
            }

            if (recommendedProducts && recommendedProducts?.slice(0, 3)?.length !== 0) {
                handleModalHeight(true);
            }

            handleModalHeight(false);
        }
    }, [accessories, recommendedProducts]);

    if (!cartLine) {
        return null;
    }

    return (
        <>
            {accessories?.length &&
            accessories?.length >= catalogSettingSettingsSettings.minimumAccessoryProductCount ? (
                <>
                    <Typography variant="h4" {...styles.accessoriesTitle}>
                        {translate("Accessories")}
                    </Typography>
                    <GridContainer>
                        {accessories?.slice(0, 3).map((product: any) => (
                            <>
                                {product.productDto && (
                                    <GridItem width={[12, 12, 12, 12, 4]}>
                                        <GridContainer gap={30}>
                                            <GridItem width={[4, 3, 3, 3, 4]} {...styles.productImgGridItem}>
                                                <Clickable onClick={() => onClick(product.productDto.productDetailUrl)}>
                                                    <ProductImage product={product.productDto} />
                                                </Clickable>
                                            </GridItem>
                                            <GridItem width={[8, 9, 9, 9, 8]} {...styles.productCategoryNameGridItem}>
                                                <Link
                                                    {...styles.productCategoryStyles}
                                                    href={product.productDto?.properties?.productcategoryurl}
                                                    onClick={() => onClick(product.productDto.productDetailUrl)}
                                                >
                                                    {product.productDto.properties.productcategory}
                                                </Link>
                                                <StyledWrapper {...styles.productNameItemStyles}>
                                                    <Typography {...styles.productNameStyles} as="span">
                                                        {product.productDto.name}
                                                    </Typography>
                                                </StyledWrapper> 
                                            </GridItem>
                                            <GridItem width={[4, 3, 3, 3, 4]} {...styles.gridItems}>
                                                <Typography {...styles.modelNumberErpNumberStyles} as="span">
                                                    {product.productDto.modelNumber}
                                                </Typography>
                                                <Typography {...styles.modelNumberErpNumberStyles} as="span">
                                                    {product.productDto.erpNumber}
                                                </Typography>
                                            </GridItem>
                                            <GridItem width={[8, 9, 9, 9, 8]} {...styles.gridItems}>
                                                <Button
                                                    {...styles.viewProductButtonStyles}
                                                    variant="secondary"
                                                    onClick={() => onClick(product.productDto.productDetailUrl)} 
                                                >
                                                    {translate("View Product")}
                                                </Button>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                )}
                            </>
                        ))}
                    </GridContainer>
                </>
            ) : (
                <>
                    {recommendedProducts !== null && (
                        <Typography variant="h4" {...styles.accessoriesTitle}>
                            {translate("Recommended for You")}
                        </Typography>
                    )}
                    <GridContainer>
                        {recommendedProducts?.slice(0, 3).map((product: any) => (
                            <>
                                <GridItem width={[12, 12, 12, 12, 4]}>
                                    <GridContainer gap={30}>
                                        <GridItem width={[4, 3, 3, 3, 4]} {...styles.productImgGridItem}>
                                            <Clickable onClick={() => onClick(product.productDetailUrl==null?product.urlSegment:product.productDetailUrl)}>
                                                <ProductImage product={product} />
                                            </Clickable>
                                        </GridItem>
                                        <GridItem width={[8, 9, 9, 9, 8]} {...styles.productCategoryNameGridItem}>
                                            <Link
                                                {...styles.productCategoryStyles}
                                                href={"/catalog" +product.brand.detailPagePath}
                                            >
                                                {product.brand.urlSegment}
                                            </Link>
                                            <Typography {...styles.productNameStyles} as="span">
                                                {product.name}
                                            </Typography>
                                        </GridItem>
                                        <GridItem width={[4, 3, 3, 3, 4]} {...styles.gridItems}>
                                            <Typography {...styles.modelNumberErpNumberStyles} as="span">
                                                {product.modelNumber}
                                            </Typography>
                                            <Typography {...styles.modelNumberErpNumberStyles} as="span">
                                                {product.erpNumber}
                                            </Typography>
                                        </GridItem>
                                        <GridItem width={[8, 9, 9, 9, 8]} {...styles.gridItems}>
                                            <Button
                                             {...styles.viewProductButtonStyles}
                                                variant="secondary"
                                                onClick={() => onClick(product.productDetailUrl==null?product.urlSegment:product.productDetailUrl)}
                                            >
                                                {translate("View Product")}
                                            </Button>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            </>
                        ))}
                    </GridContainer>
                </>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCartAccessories));
